import { RESERVED_USERNAMES, WEB_PUBLIC_ROOT_ROUTE_SEGMENTS } from './reserved-usernames';
import { UNETHICAL_USERNAMES } from './unethical-usernames';

/** Username claim format: lowercase letters, digits, underscore; 3–30 chars. */
export const USERNAME_FORMAT = /^[a-z0-9_]{3,30}$/;

export type UsernameAvailabilityReason = 'invalid_format' | 'reserved' | 'taken';

export function normalizeUsername(username: string): string {
  return username.trim().toLowerCase();
}

export function isValidUsernameFormat(username: string): boolean {
  return USERNAME_FORMAT.test(normalizeUsername(username));
}

/** True when the handle is platform-reserved or prohibited (exact match). */
export function isReservedUsername(username: string): boolean {
  const normalized = normalizeUsername(username);
  if (!normalized) return false;
  return RESERVED_USERNAMES.has(normalized) || UNETHICAL_USERNAMES.has(normalized);
}

/**
 * Claim check for signup or username change (format + reserved).
 * Grandfather: a user may keep their current reserved username; they cannot claim a different reserved one.
 * Uniqueness (`taken`) is enforced by the API against the database.
 */
export function evaluateUsernameClaim(
  username: string,
  options?: { currentUsername?: string | null },
): { ok: true; username: string } | { ok: false; reason: 'invalid_format' | 'reserved' } {
  const normalized = normalizeUsername(username);
  if (!isValidUsernameFormat(normalized)) {
    return { ok: false, reason: 'invalid_format' };
  }
  const current = options?.currentUsername
    ? normalizeUsername(options.currentUsername)
    : null;
  if (isReservedUsername(normalized) && normalized !== current) {
    return { ok: false, reason: 'reserved' };
  }
  return { ok: true, username: normalized };
}

/** Static first-path segments for vanity-shell detection (Set view of WEB_PUBLIC_ROOT_ROUTE_SEGMENTS). */
export const WEB_PUBLIC_STATIC_ROOT_SEGMENTS: ReadonlySet<string> = new Set(
  WEB_PUBLIC_ROOT_ROUTE_SEGMENTS,
);

export {
  RESERVED_USERNAMES,
  WEB_PUBLIC_ROOT_ROUTE_SEGMENTS,
  claimableFormsFromRouteSegment,
} from './reserved-usernames';
export { UNETHICAL_USERNAMES } from './unethical-usernames';
export { canExposePublicSocialProfile } from './public-profile';
